import { Component, EventEmitter, OnInit, Output, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ChemicalsService } from '../../../core/services/chemicals.service';
import { WebSocketService } from '../../../web-socket-handlers/web-socket.service';
import {
  type ChemicalImportRow,
  type ChemicalImportProgressEvent,
  type ChemicalImportCompleteEvent,
  type ChemicalImportFailedEvent
} from '../../../core/models/chemical.model';

declare var bootstrap: any;

interface ParsedRow extends ChemicalImportRow {
  rowNumber: number;
  isValid: boolean;
  errors: string[];
}

interface ValidationPreview {
  totalRows: number;
  validRows: number;
  invalidRows: number;
  rows: ParsedRow[];
}

/**
 * Chemical Import Modal Component
 *
 * Modal for importing chemicals from CSV file with validation preview.
 * Uses WebSocket for real-time progress updates during async import.
 */
@Component({
  selector: 'app-chemical-import-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './chemical-import-modal.component.html',
  styleUrl: './chemical-import-modal.component.scss'
})
export class ChemicalImportModalComponent implements OnInit, OnDestroy {
  @Output() close = new EventEmitter<void>();
  @Output() importSuccess = new EventEmitter<{
    imported: number;
    skipped: number;
    enrichment?: { matched: number; unmatched: number } | null;
  }>();

  loading = false;
  importing = false;
  errorMessage = '';

  // File selection
  selectedFile: File | null = null;

  // CSV data
  csvData = '';
  preview: ValidationPreview | null = null;

  // Import options
  enrichFromEpa = true;  // Default to true - auto-populate from EPA data

  // Import progress tracking
  currentJobId: string | null = null;
  importProgress = 0;
  importStatus: 'idle' | 'queued' | 'importing' | 'enriching' | 'complete' | 'failed' = 'idle';
  importedCount = 0;
  totalCount = 0;
  currentRowName = '';

  // Valid categories and signal words
  private readonly validCategories = ['herbicide', 'insecticide', 'fungicide', 'fertilizer', 'adjuvant', 'igr', 'miticide', 'pgr', 'other'];
  private readonly validSignalWords = ['CAUTION', 'WARNING', 'DANGER'];
  // Supports: XXX-XXX, XXXXX-XX, XXXXX-XXX, XXXXX-XXXX, XXXXX-XX-XXXXX
  private readonly epaNumberPattern = /^\d{3}-\d{3}$|^\d{5}-\d{1,4}$|^\d{5}-\d{1,4}-\d{5}$/;

  // Column name mappings (flexible header detection)
  private readonly columnAliases: Record<string, string[]> = {
    name: ['name', 'product', 'productname', 'product_name', 'chemical', 'chemicalname', 'chemical_name'],
    epaNumber: ['epanumber', 'epa_number', 'epa', 'registrationnumber', 'registration_number', 'reg_number', 'regnumber'],
    manufacturer: ['manufacturer', 'mfg', 'company', 'brand'],
    activeIngredient: ['activeingredient', 'active_ingredient', 'ai', 'ingredient', 'active'],
    signalWord: ['signalword', 'signal_word', 'signal', 'toxicity'],
    rei: ['rei', 'reentry', 're_entry', 'reentryinterval', 'reentry_interval'],
    phi: ['phi', 'preharvest', 'pre_harvest', 'preharvestinterval', 'preharvest_interval'],
    category: ['category', 'type', 'chemicaltype', 'chemical_type', 'producttype', 'product_type', 'class'],
    formulation: ['formulation', 'form', 'physicalform', 'physical_form'],
    defaultRate: ['defaultrate', 'default_rate', 'rate', 'applicationrate', 'application_rate'],
    targetPests: ['targetpests', 'target_pests', 'pests', 'targets'],
    crops: ['crops', 'sites', 'usesites', 'use_sites'],
    compareTo: ['compareto', 'compare_to', 'equivalent', 'brandequivalent', 'brand_equivalent'],
    rupFlag: ['rupflag', 'rup_flag', 'rup', 'restricteduse', 'restricted_use', 'restricted'],
    sponsor: ['sponsor', 'sponsorname', 'sponsor_name', 'partner'],
    sponsorWebsite: ['sponsorwebsite', 'sponsor_website', 'website', 'url'],
    sponsorPhone: ['sponsorphone', 'sponsor_phone', 'phone'],
    sponsorEmail: ['sponsoremail', 'sponsor_email', 'email'],
    isFeatured: ['isfeatured', 'is_featured', 'featured'],
    specialOffer: ['specialoffer', 'special_offer', 'offer', 'promo', 'promotion']
  };

  // Detected column indices from header
  private columnIndices: Record<string, number> = {};

  private modal: any;

  constructor(
    private chemicalsService: ChemicalsService,
    private webSocketService: WebSocketService
  ) {}

  ngOnInit(): void {
    // Register WebSocket event handlers
    this.registerWebSocketHandlers();

    // Open modal on init
    setTimeout(() => {
      const modalElement = document.getElementById('chemicalImportModal');
      if (modalElement) {
        this.modal = new bootstrap.Modal(modalElement, {
          backdrop: 'static',
          keyboard: false
        });
        this.modal.show();

        // Handle modal hidden event
        modalElement.addEventListener('hidden.bs.modal', () => {
          this.close.emit();
        });
      }
    }, 0);
  }

  ngOnDestroy(): void {
    if (this.modal) {
      this.modal.hide();
    }
  }

  /**
   * Register WebSocket event handlers for import progress
   */
  private registerWebSocketHandlers(): void {
    // Progress updates
    this.webSocketService.registerEventHandler('chemical-import:progress', (event: ChemicalImportProgressEvent) => {
      if (event.jobId === this.currentJobId) {
        this.importProgress = event.progress;
        this.importStatus = event.status;
        this.importedCount = event.imported;
        this.totalCount = event.total;
        this.currentRowName = event.currentRow || '';
      }
    });

    // Import complete
    this.webSocketService.registerEventHandler('chemical-import:complete', (event: ChemicalImportCompleteEvent) => {
      if (event.jobId === this.currentJobId) {
        this.importing = false;
        this.importStatus = 'complete';
        this.importProgress = 100;

        // Emit success event
        this.importSuccess.emit({
          imported: event.imported,
          skipped: event.skipped,
          enrichment: event.enrichment
        });

        // Close modal after short delay to show completion
        setTimeout(() => {
          this.closeModal();
        }, 1500);
      }
    });

    // Import failed
    this.webSocketService.registerEventHandler('chemical-import:failed', (event: ChemicalImportFailedEvent) => {
      if (event.jobId === this.currentJobId) {
        this.importing = false;
        this.importStatus = 'failed';
        this.errorMessage = event.error || 'Import failed';
      }
    });
  }

  /**
   * Close modal
   */
  closeModal(): void {
    if (this.modal) {
      this.modal.hide();
    }
    this.close.emit();
  }

  /**
   * Download CSV template
   */
  downloadTemplate(): void {
    this.chemicalsService.getImportTemplate().subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'chemical-import-template.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      },
      error: (error) => {
        console.error('Failed to download template:', error);
        this.errorMessage = 'Failed to download template';
      }
    });
  }

  /**
   * Handle file selection
   */
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];

      // Validate file type
      if (!file.name.endsWith('.csv')) {
        this.errorMessage = 'Please select a CSV file';
        return;
      }

      this.selectedFile = file;
      this.errorMessage = '';

      // Read file contents
      const reader = new FileReader();
      reader.onload = (e) => {
        this.csvData = e.target?.result as string;
        this.parseAndValidate();
      };
      reader.readAsText(file);
    }
  }

  /**
   * Parse CSV and validate rows
   */
  parseAndValidate(): void {
    if (!this.csvData.trim()) {
      this.preview = null;
      return;
    }

    const lines = this.csvData.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      this.errorMessage = 'CSV file must have at least one data row';
      this.preview = null;
      return;
    }

    // Parse header row to detect column positions
    const headerFields = this.parseCsvLine(lines[0]);
    this.detectColumnIndices(headerFields);

    // Skip header row
    const dataLines = lines.slice(1);
    const rows: ParsedRow[] = [];

    for (let i = 0; i < dataLines.length; i++) {
      const fields = this.parseCsvLine(dataLines[i]);
      const parsedRow = this.parseRow(fields, i + 2); // +2 because row 1 is header, and we start from 0
      rows.push(parsedRow);
    }

    this.preview = {
      totalRows: rows.length,
      validRows: rows.filter(r => r.isValid).length,
      invalidRows: rows.filter(r => !r.isValid).length,
      rows
    };
  }

  /**
   * Detect column indices from header row using flexible matching
   */
  private detectColumnIndices(headerFields: string[]): void {
    this.columnIndices = {};

    for (let i = 0; i < headerFields.length; i++) {
      const headerNormalized = headerFields[i].toLowerCase().replace(/[^a-z0-9]/g, '');

      // Find which field this column maps to
      for (const [fieldName, aliases] of Object.entries(this.columnAliases)) {
        if (aliases.includes(headerNormalized)) {
          this.columnIndices[fieldName] = i;
          break;
        }
      }
    }
  }

  /**
   * Get field value from row using detected column index
   */
  private getFieldValue(fields: string[], fieldName: string): string | undefined {
    const index = this.columnIndices[fieldName];
    if (index === undefined || index >= fields.length) {
      return undefined;
    }
    const value = fields[index]?.trim();
    return value || undefined;
  }

  /**
   * Parse a single CSV line handling quoted fields
   */
  private parseCsvLine(line: string): string[] {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }

    result.push(current.trim());
    return result;
  }

  /**
   * Parse row into ChemicalImportRow and validate
   * Uses flexible column detection from header
   */
  private parseRow(fields: string[], rowNumber: number): ParsedRow {
    const errors: string[] = [];

    // Map fields using detected column indices (flexible mapping)
    const row: ParsedRow = {
      rowNumber,
      name: this.getFieldValue(fields, 'name') || '',
      epaNumber: this.getFieldValue(fields, 'epaNumber'),
      manufacturer: this.getFieldValue(fields, 'manufacturer'),
      activeIngredient: this.getFieldValue(fields, 'activeIngredient'),
      signalWord: this.getFieldValue(fields, 'signalWord') || '',
      rei: this.getFieldValue(fields, 'rei'),
      phi: this.getFieldValue(fields, 'phi'),
      category: this.getFieldValue(fields, 'category') || '',
      formulation: this.getFieldValue(fields, 'formulation'),
      defaultRate: this.getFieldValue(fields, 'defaultRate'),
      targetPests: this.getFieldValue(fields, 'targetPests'),
      crops: this.getFieldValue(fields, 'crops'),
      sponsor: this.getFieldValue(fields, 'sponsor'),
      sponsorName: this.getFieldValue(fields, 'sponsor'),  // Also use sponsor column for sponsorName
      sponsorWebsite: this.getFieldValue(fields, 'sponsorWebsite'),
      sponsorPhone: this.getFieldValue(fields, 'sponsorPhone'),
      sponsorEmail: this.getFieldValue(fields, 'sponsorEmail'),
      isFeatured: this.getFieldValue(fields, 'isFeatured'),
      specialOffer: this.getFieldValue(fields, 'specialOffer'),
      isValid: true,
      errors: []
    };

    // Also capture compareTo and rupFlag for the new fields
    (row as any).compareTo = this.getFieldValue(fields, 'compareTo');
    (row as any).rupFlag = this.getFieldValue(fields, 'rupFlag');

    // Validate required fields
    if (!row.name.trim()) {
      errors.push('Name is required');
    }

    // Signal word is OPTIONAL - only validate format if provided
    if (row.signalWord?.trim()) {
      if (!this.validSignalWords.includes(row.signalWord.toUpperCase())) {
        errors.push(`Signal Word must be one of: ${this.validSignalWords.join(', ')}`);
      }
    }

    if (!row.category.trim()) {
      errors.push('Category is required');
    } else if (!this.validCategories.includes(row.category.toLowerCase())) {
      errors.push(`Category must be one of: ${this.validCategories.join(', ')}`);
    }

    // Validate EPA number format if provided
    if (row.epaNumber && !this.epaNumberPattern.test(row.epaNumber)) {
      errors.push('Invalid EPA Number format');
    }

    row.errors = errors;
    row.isValid = errors.length === 0;

    return row;
  }

  /**
   * Check if import can proceed
   */
  get canImport(): boolean {
    return !!this.preview && this.preview.validRows > 0 && !this.importing;
  }

  /**
   * Get status message for progress display
   */
  get statusMessage(): string {
    switch (this.importStatus) {
      case 'queued':
        return 'Import queued, waiting to start...';
      case 'importing':
        return this.currentRowName
          ? `Importing: ${this.currentRowName} (${this.importedCount}/${this.totalCount})`
          : `Importing chemicals... (${this.importedCount}/${this.totalCount})`;
      case 'enriching':
        return 'Looking up EPA data...';
      case 'complete':
        return 'Import complete!';
      case 'failed':
        return 'Import failed';
      default:
        return '';
    }
  }

  /**
   * Execute import
   */
  importChemicals(): void {
    if (!this.canImport || !this.preview) return;

    this.importing = true;
    this.importStatus = 'queued';
    this.importProgress = 0;
    this.errorMessage = '';

    // Prepare valid rows for import
    const validRows: ChemicalImportRow[] = this.preview.rows
      .filter(r => r.isValid)
      .map(r => ({
        name: r.name,
        epaNumber: r.epaNumber,
        manufacturer: r.manufacturer,
        activeIngredient: r.activeIngredient,
        signalWord: r.signalWord?.trim() ? r.signalWord.toUpperCase() : undefined,
        rei: r.rei,
        phi: r.phi,
        category: r.category.toLowerCase(),
        formulation: r.formulation,
        defaultRate: r.defaultRate,
        targetPests: r.targetPests,
        crops: r.crops,
        compareTo: (r as any).compareTo,
        rupFlag: (r as any).rupFlag,
        sponsor: r.sponsor || r.sponsorName,
        sponsorName: r.sponsorName,
        sponsorWebsite: r.sponsorWebsite,
        sponsorPhone: r.sponsorPhone,
        sponsorEmail: r.sponsorEmail,
        isFeatured: r.isFeatured,
        specialOffer: r.specialOffer
      }));

    this.totalCount = validRows.length;

    this.chemicalsService.importChemicals(validRows, { enrichFromEpa: this.enrichFromEpa }).subscribe({
      next: (result) => {
        // Job queued successfully - store job ID and wait for WebSocket events
        this.currentJobId = result.jobId;
        console.log(`Import job ${result.jobId} queued for ${result.totalRows} chemicals`);
      },
      error: (error) => {
        console.error('Failed to queue import:', error);
        this.errorMessage = error?.error?.error || 'Failed to start import';
        this.importing = false;
        this.importStatus = 'failed';
      }
    });
  }
}
